import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { emailSequenceService } from '../../../services/emailSequenceService';
import { notificationService } from '../../../services/notificationService';

const EmailSequenceManager = () => {
  const [sequences, setSequences] = useState([]);
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showStepModal, setShowStepModal] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [trackingData, setTrackingData] = useState([]);
  const [activeTab, setActiveTab] = useState('sequences');
  const [liveUpdateIndicator, setLiveUpdateIndicator] = useState(false);

  const [newSequence, setNewSequence] = useState({
    name: '',
    description: '',
    formType: 'contact',
    isActive: true
  });

  const [newStep, setNewStep] = useState({
    stepOrder: 1,
    delayHours: 24,
    subjectTemplate: '',
    htmlTemplate: '',
    isActive: true
  });

  useEffect(() => {
    loadSequences();
    loadTrackingData();

    // Subscribe to real-time email tracking updates
    const trackingChannel = notificationService?.subscribeToEmailTracking((payload) => {
      if (payload?.eventType === 'INSERT') {
        // New email scheduled
        setTrackingData(prev => [payload?.new, ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        // Email status updated (sent, failed, etc.)
        setTrackingData(prev => 
          prev?.map(track => 
            track?.id === payload?.new?.id ? payload?.new : track
          )
        );
      }

      // Show live update indicator
      setLiveUpdateIndicator(true);
      setTimeout(() => setLiveUpdateIndicator(false), 3000);
    });

    // Cleanup subscription
    return () => {
      notificationService?.unsubscribe(trackingChannel);
    };
  }, []);

  const loadSequences = async () => {
    try {
      setLoading(true);
      const data = await emailSequenceService?.getAllSequences();
      setSequences(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load sequences');
    } finally {
      setLoading(false);
    }
  };

  const loadTrackingData = async () => {
    try {
      const data = await emailSequenceService?.getAllTracking();
      setTrackingData(data || []);
    } catch (err) {
      console.error('Failed to load tracking data:', err);
    }
  };

  const handleCreateSequence = async (e) => {
    e?.preventDefault();
    try {
      await emailSequenceService?.createSequence(newSequence);
      setShowCreateModal(false);
      setNewSequence({ name: '', description: '', formType: 'contact', isActive: true });
      loadSequences();
    } catch (err) {
      setError(err?.message || 'Failed to create sequence');
    }
  };

  const handleToggleSequence = async (sequenceId, currentStatus) => {
    try {
      await emailSequenceService?.updateSequence(sequenceId, { isActive: !currentStatus });
      loadSequences();
    } catch (err) {
      setError(err?.message || 'Failed to update sequence');
    }
  };

  const handleDeleteSequence = async (sequenceId) => {
    if (!window.confirm('Are you sure you want to delete this sequence? All scheduled emails will be cancelled.')) return;

    try {
      await emailSequenceService?.deleteSequence(sequenceId);
      setSelectedSequence(null);
      loadSequences();
    } catch (err) {
      setError(err?.message || 'Failed to delete sequence');
    }
  };

  const handleCreateStep = async (e) => {
    e?.preventDefault();
    try {
      await emailSequenceService?.createStep({
        ...newStep,
        sequenceId: selectedSequence?.id
      });
      setShowStepModal(false);
      setNewStep({ stepOrder: 1, delayHours: 24, subjectTemplate: '', htmlTemplate: '', isActive: true });
      
      // Reload selected sequence
      const updated = await emailSequenceService?.getSequenceById(selectedSequence?.id);
      setSelectedSequence(updated);
      loadSequences();
    } catch (err) {
      setError(err?.message || 'Failed to create step');
    }
  };

  const handleUpdateStep = async (e) => {
    e?.preventDefault();
    try {
      await emailSequenceService?.updateStep(editingStep?.id, newStep);
      setShowStepModal(false);
      setEditingStep(null);
      setNewStep({ stepOrder: 1, delayHours: 24, subjectTemplate: '', htmlTemplate: '', isActive: true });
      
      // Reload selected sequence
      const updated = await emailSequenceService?.getSequenceById(selectedSequence?.id);
      setSelectedSequence(updated);
      loadSequences();
    } catch (err) {
      setError(err?.message || 'Failed to update step');
    }
  };

  const handleDeleteStep = async (stepId) => {
    if (!window.confirm('Are you sure you want to delete this step?')) return;

    try {
      await emailSequenceService?.deleteStep(stepId);
      
      // Reload selected sequence
      const updated = await emailSequenceService?.getSequenceById(selectedSequence?.id);
      setSelectedSequence(updated);
      loadSequences();
    } catch (err) {
      setError(err?.message || 'Failed to delete step');
    }
  };

  const openEditStepModal = (step) => {
    setEditingStep(step);
    setNewStep({
      stepOrder: step?.step_order,
      delayHours: step?.delay_hours,
      subjectTemplate: step?.subject_template,
      htmlTemplate: step?.html_template,
      isActive: step?.is_active
    });
    setShowStepModal(true);
  };

  const getFormTypeLabel = (formType) => {
    const labels = {
      contact: 'Contact Form',
      consultation: 'Consultation',
      partnership: 'Partnership'
    };
    return labels?.[formType] || formType;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-600 bg-yellow-50',
      sent: 'text-green-600 bg-green-50',
      failed: 'text-red-600 bg-red-50',
      cancelled: 'text-gray-600 bg-gray-50'
    };
    return colors?.[status] || 'text-gray-600 bg-gray-50';
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Email Sequences</h2>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">Automated follow-up campaigns to nurture leads</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="touch-manipulation w-full sm:w-auto">
          <Icon name="Plus" className="w-4 h-4 mr-2" />
          <span className="hidden sm:inline">Create Sequence</span>
          <span className="sm:hidden">Create</span>
        </Button>
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}
      {/* Live Update Indicator */}
      {liveUpdateIndicator && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-lg w-fit"
        >
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-sm text-green-500 font-medium">Email tracking updated</span>
        </motion.div>
      )}
      {/* Tabs */}
      <div className="border-b border-gray-200 overflow-x-auto">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('sequences')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'sequences' ?'border-primary text-primary' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Sequences ({sequences?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('tracking')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'tracking' ?'border-primary text-primary' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Email Tracking ({trackingData?.length || 0})
          </button>
        </nav>
      </div>
      {/* Content */}
      {activeTab === 'sequences' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Sequences List */}
          <div className="lg:col-span-1 space-y-4">
            {sequences?.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <Icon name="Mail" className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No sequences created yet</p>
                <Button onClick={() => setShowCreateModal(true)} className="mt-4">
                  Create Your First Sequence
                </Button>
              </div>
            ) : (
              sequences?.map((sequence) => (
                <motion.div
                  key={sequence?.id}
                  whileHover={{ scale: 1.02 }}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedSequence?.id === sequence?.id
                      ? 'border-primary bg-primary/5' :'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedSequence(sequence)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{sequence?.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{sequence?.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                          {getFormTypeLabel(sequence?.form_type)}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          sequence?.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {sequence?.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {sequence?.email_sequence_steps?.length || 0} steps
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>

          {/* Sequence Details */}
          <div className="lg:col-span-2">
            {selectedSequence ? (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{selectedSequence?.name}</h3>
                    <p className="text-gray-600 mt-1">{selectedSequence?.description}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleSequence(selectedSequence?.id, selectedSequence?.is_active)}
                    >
                      {selectedSequence?.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteSequence(selectedSequence?.id)}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Icon name="Trash2" className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Steps */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-900">Email Steps</h4>
                    <Button
                      size="sm"
                      onClick={() => {
                        setEditingStep(null);
                        setNewStep({
                          stepOrder: (selectedSequence?.email_sequence_steps?.length || 0) + 1,
                          delayHours: 24,
                          subjectTemplate: '',
                          htmlTemplate: '',
                          isActive: true
                        });
                        setShowStepModal(true);
                      }}
                    >
                      <Icon name="Plus" className="w-4 h-4 mr-2" />
                      Add Step
                    </Button>
                  </div>

                  {selectedSequence?.email_sequence_steps?.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <p className="text-gray-600">No steps added yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {selectedSequence?.email_sequence_steps
                        ?.sort((a, b) => a?.step_order - b?.step_order)
                        ?.map((step, index) => (
                          <div
                            key={step?.id}
                            className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="text-sm font-semibold text-gray-900">
                                    Step {step?.step_order}
                                  </span>
                                  <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                    {step?.delay_hours}h delay
                                  </span>
                                  {!step?.is_active && (
                                    <span className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded">
                                      Inactive
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm font-medium text-gray-900 mb-1">
                                  {step?.subject_template}
                                </p>
                                <p className="text-xs text-gray-600 line-clamp-2">
                                  {step?.html_template?.replace(/<[^>]*>/g, '')?.substring(0, 150)}...
                                </p>
                              </div>
                              <div className="flex gap-2 ml-4">
                                <button
                                  onClick={() => openEditStepModal(step)}
                                  className="p-2 text-gray-600 hover:text-primary hover:bg-white rounded"
                                >
                                  <Icon name="Edit" className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteStep(step?.id)}
                                  className="p-2 text-gray-600 hover:text-red-600 hover:bg-white rounded"
                                >
                                  <Icon name="Trash2" className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
                <Icon name="Mail" className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Select a sequence to view details</p>
              </div>
            )}
          </div>
        </div>
      )}
      {activeTab === 'tracking' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recipient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sequence
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Step
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scheduled
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sent
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {trackingData?.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                      No tracking data available
                    </td>
                  </tr>
                ) : (
                  trackingData?.map((track) => (
                    <tr key={track?.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {track?.form_submissions?.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {track?.form_submissions?.email}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{track?.email_sequences?.name}</div>
                        <div className="text-xs text-gray-500">
                          {getFormTypeLabel(track?.email_sequences?.form_type)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          Step {track?.email_sequence_steps?.step_order}
                        </div>
                        <div className="text-xs text-gray-500 max-w-xs truncate">
                          {track?.email_sequence_steps?.subject_template}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(track?.status)}`}>
                          {track?.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(track?.scheduled_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {track?.sent_at ? formatDate(track?.sent_at) : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Create Sequence Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white rounded-lg p-6 max-w-md w-full"
              onClick={(e) => e?.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">Create Email Sequence</h3>
              <form onSubmit={handleCreateSequence} className="space-y-4">
                <Input
                  label="Sequence Name"
                  value={newSequence?.name}
                  onChange={(e) => setNewSequence({ ...newSequence, name: e?.target?.value })}
                  required
                  placeholder="e.g., Consultation Follow-up"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={newSequence?.description}
                    onChange={(e) => setNewSequence({ ...newSequence, description: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    rows="3"
                    placeholder="Describe the purpose of this sequence"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Form Type
                  </label>
                  <select
                    value={newSequence?.formType}
                    onChange={(e) => setNewSequence({ ...newSequence, formType: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="contact">Contact Form</option>
                    <option value="consultation">Consultation</option>
                    <option value="partnership">Partnership</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={newSequence?.isActive}
                    onChange={(e) => setNewSequence({ ...newSequence, isActive: e?.target?.checked })}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <label htmlFor="isActive" className="text-sm text-gray-700">
                    Activate immediately
                  </label>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1">
                    Create Sequence
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Create/Edit Step Modal */}
      <AnimatePresence>
        {showStepModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowStepModal(false);
              setEditingStep(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e?.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {editingStep ? 'Edit Step' : 'Add Step'}
              </h3>
              <form onSubmit={editingStep ? handleUpdateStep : handleCreateStep} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Step Order"
                    type="number"
                    min="1"
                    value={newStep?.stepOrder}
                    onChange={(e) => setNewStep({ ...newStep, stepOrder: parseInt(e?.target?.value) })}
                    required
                  />
                  <Input
                    label="Delay (hours)"
                    type="number"
                    min="1"
                    value={newStep?.delayHours}
                    onChange={(e) => setNewStep({ ...newStep, delayHours: parseInt(e?.target?.value) })}
                    required
                  />
                </div>
                <Input
                  label="Subject Template"
                  value={newStep?.subjectTemplate}
                  onChange={(e) => setNewStep({ ...newStep, subjectTemplate: e?.target?.value })}
                  required
                  placeholder="Use {{name}}, {{email}}, {{metadata.field}} for dynamic content"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    HTML Template
                  </label>
                  <textarea
                    value={newStep?.htmlTemplate}
                    onChange={(e) => setNewStep({ ...newStep, htmlTemplate: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
                    rows="10"
                    required
                    placeholder="HTML email template with {{placeholders}}"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Available fields: name, email, phone, subject, message, metadata.* (e.g., metadata.service)
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="stepActive"
                    checked={newStep?.isActive}
                    onChange={(e) => setNewStep({ ...newStep, isActive: e?.target?.checked })}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <label htmlFor="stepActive" className="text-sm text-gray-700">
                    Active
                  </label>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowStepModal(false);
                      setEditingStep(null);
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1">
                    {editingStep ? 'Update Step' : 'Add Step'}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EmailSequenceManager;